/**
 * `<currency-selector>` — visitor-facing UI for the custom currency converter.
 *
 * Mirrors `<dropdown-localization-component>` from Horizon's native localization
 * form: same trigger button + caret + popover panel + list-item layout. Reuses
 * the existing `localization-form__*` CSS so the visual match is automatic.
 *
 * Two render styles, both produced by `snippets/currency-selector.liquid`:
 *   - `dropdown` — desktop header inline trigger
 *   - `drawer`   — mobile header drawer accordion row
 *
 * The component dispatches a single `currency:change` document event when the
 * visitor picks a currency. `currency-converter.js` listens for it and
 * re-converts the page. The selector also listens for `currency:applied` and
 * `currency:ready` so it can update its visible label and `aria-current` state
 * when boot-time auto-detection picks a currency for the visitor.
 *
 * @module currency-selector
 */

import { Component } from '@theme/component';
import { getStoredChoice } from '@theme/currency-geolocation';

/**
 * @typedef {Object} CurrencySelectorRefs
 * @property {HTMLButtonElement} [button]
 * @property {HTMLElement} [panel]
 * @property {HTMLElement} [activeLabel]
 * @property {HTMLElement[]} [items]
 */

/**
 * @extends {Component<CurrencySelectorRefs>}
 */
class CurrencySelector extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener('currency:ready', this.#handleConverterUpdate, { signal });
    document.addEventListener('currency:applied', this.#handleConverterUpdate, { signal });
    document.addEventListener('click', this.#handleOutsideClick, { signal });

    // Sync from localStorage immediately. The converter is the source of truth
    // for the active currency, but its `#boot()` is async — so on every page
    // navigation the selector would briefly show "MYR" until `currency:ready`
    // dispatched. Reading the persisted choice synchronously here makes the
    // label match the actual displayed currency on first paint, with no flash.
    // The event listeners above keep us in sync when the converter changes
    // its mind (e.g. after IP detection on a fresh visitor) or when another
    // selector instance dispatches `currency:change`.
    const stored = getStoredChoice();
    if (stored) this.#updateActiveLabel(stored);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // ---------------------------------------------------------------------------
  // Declarative `on:click` handlers — bound by the Component base class.
  // ---------------------------------------------------------------------------

  /**
   * Toggle the dropdown panel open/closed.
   */
  toggleSelector = () => {
    const { panel, button } = this.refs;
    if (!panel || !button) return;
    const willOpen = panel.hasAttribute('hidden');
    if (willOpen) {
      panel.removeAttribute('hidden');
      button.setAttribute('aria-expanded', 'true');
    } else {
      panel.setAttribute('hidden', '');
      button.setAttribute('aria-expanded', 'false');
    }
  };

  /**
   * Selects the currency identified by the action's path segment, e.g. `/selectCurrency/USD`.
   * Horizon's declarative `on:click` dispatches handlers as `(...pathArgs, event)`,
   * so the path segment comes first and the event is last — see `localization.js`
   * `selectCountry(countryName, event)` for the convention.
   * @param {string} currency
   * @param {Event} event
   */
  selectCurrency = (currency, event) => {
    if (event && typeof (/** @type {any} */ (event)).preventDefault === 'function') {
      event.preventDefault();
    }
    if (!currency) return;
    document.dispatchEvent(
      new CustomEvent('currency:change', {
        bubbles: true,
        detail: { currency },
      })
    );
    this.#updateActiveLabel(currency);
    this.#closePanel();
  };

  /**
   * Drawer mode uses `<details>` whose toggle event fires on user expand. No-op
   * for now; we keep the binding so the snippet stays consistent with Horizon's
   * native drawer-localization-component conventions.
   */
  toggle = () => {
    // intentional no-op
  };

  // ---------------------------------------------------------------------------
  // Reactions to converter state
  // ---------------------------------------------------------------------------

  #handleConverterUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || typeof detail.active !== 'string') return;
    this.#updateActiveLabel(detail.active);
  };

  /** @param {string} currency */
  #updateActiveLabel(currency) {
    const { activeLabel, items } = this.refs;
    if (activeLabel) activeLabel.textContent = currency;
    if (items) {
      for (const item of items) {
        if (item.dataset.currency === currency) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      }
    }
  }

  #closePanel() {
    // Desktop dropdown: hidden attribute on the panel + aria-expanded on button.
    const { panel, button } = this.refs;
    if (panel && !panel.hasAttribute('hidden')) panel.setAttribute('hidden', '');
    if (button) button.setAttribute('aria-expanded', 'false');

    // Drawer dropdown: native `<details>` controlled by the `[open]` attribute.
    // Removing it collapses the popover after a currency pick so the drawer
    // doesn't stay expanded with the list still showing.
    const details = this.querySelector('details');
    if (details && details.hasAttribute('open')) {
      details.removeAttribute('open');
    }
  }

  /**
   * Closing the dropdown when the visitor clicks elsewhere matches the native
   * `<dropdown-localization-component>` behavior. Drawer mode handles its own
   * close via `<details>` and this is a no-op for it.
   * @param {Event} event
   */
  #handleOutsideClick = (event) => {
    if (this.dataset.style === 'drawer') return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#closePanel();
  };
}

if (!customElements.get('currency-selector')) {
  customElements.define('currency-selector', CurrencySelector);
}

export { CurrencySelector };
